import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import axiosInstance from '../../utils/axiosConfig';
import useTranslation from '../../utils/useTranslation';

const VISIBLE_COUNT = 4;
const SLIDE_MS = 400;
const ITEM_GAP_FALLBACK = 20;
const ARROW_SIZE = 36;
// Allow the four logos to keep fitting (without overflow) on small phones.
const CELL_MIN = 48;

function getItemGap(panel) {
  const raw = getComputedStyle(panel).getPropertyValue('--partner-item-gap').trim();
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : ITEM_GAP_FALLBACK;
}

function getCarouselMetrics(panel) {
  const style = getComputedStyle(panel);
  const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
  const gap = parseFloat(style.gap) || 0;
  const itemGap = getItemGap(panel);
  const buttons = panel.querySelectorAll('.partners-carousel-btn');

  let flexChrome = 0;
  buttons.forEach((btn) => {
    flexChrome += btn.offsetWidth;
    const btnStyle = getComputedStyle(btn);
    flexChrome += (parseFloat(btnStyle.marginLeft) || 0) + (parseFloat(btnStyle.marginRight) || 0);
  });
  if (buttons.length > 1) {
    flexChrome += gap * (buttons.length - 1);
  }

  const innerW = panel.clientWidth - padX;
  const viewportW = Math.max(0, innerW - flexChrome);
  const dividerGaps = VISIBLE_COUNT - 1;
  let cellW = dividerGaps > 0
    ? (viewportW - dividerGaps * itemGap) / VISIBLE_COUNT
    : viewportW / VISIBLE_COUNT;
  cellW = Math.max(cellW, CELL_MIN);

  return { cellW, viewportW, itemGap };
}

function PartnerLogo({ sponsor }) {
  const logo = sponsor.image_url ? (
    <img src={sponsor.image_url} alt="" loading="lazy" />
  ) : (
    <span className="partner-carousel-fallback">{sponsor.name}</span>
  );

  const content = (
    <>
      <span className="partner-carousel-logo">{logo}</span>
      {sponsor.image_url ? (
        <span className="partner-carousel-name">{sponsor.name}</span>
      ) : null}
    </>
  );

  if (sponsor.link) {
    return (
      <a className="partner-carousel-link" href={sponsor.link} target="_blank" rel="noreferrer">
        {content}
      </a>
    );
  }

  return <div className="partner-carousel-link partner-carousel-link--static">{content}</div>;
}

export default function PartnersSection({ innerRef }) {
  const { t, lang } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startIndex, setStartIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [cellWidth, setCellWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [itemGap, setItemGap] = useState(ITEM_GAP_FALLBACK);
  const viewportRef = useRef(null);
  const panelRef = useRef(null);
  const isAnimatingRef = useRef(false);

  const isCarousel = items.length > VISIBLE_COUNT;
  const isCompact = !isCarousel && items.length <= 3;
  const maxStartIndex = Math.max(0, items.length - VISIBLE_COUNT);
  const canPrev = isCarousel && startIndex > 0;
  const canNext = isCarousel && startIndex < maxStartIndex;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get('public/sponsors/', { params: { lang } })
      .then((res) => {
        if (cancelled) return;
        setItems(res.data?.sponsors || []);
        setStartIndex(0);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [lang]);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || !isCarousel) {
      setCellWidth(0);
      setViewportWidth(0);
      setItemGap(ITEM_GAP_FALLBACK);
      return undefined;
    }

    const measure = () => {
      if (isAnimatingRef.current) return;
      const metrics = getCarouselMetrics(panel);
      setCellWidth(metrics.cellW);
      setViewportWidth(metrics.viewportW);
      setItemGap(metrics.itemGap);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(panel);
    if (panel.parentElement) ro.observe(panel.parentElement);
    return () => ro.disconnect();
  }, [isCarousel, items.length, loading]);

  const slide = useCallback((delta) => {
    if (isAnimatingRef.current) return;
    const next = startIndex + delta;
    if (next < 0 || next > maxStartIndex) return;
    isAnimatingRef.current = true;
    setIsAnimating(true);
    setStartIndex(next);
  }, [startIndex, maxStartIndex]);

  const handleTransitionEnd = useCallback((e) => {
    if (e.propertyName !== 'transform') return;
    isAnimatingRef.current = false;
    setIsAnimating(false);
  }, []);

  useEffect(() => {
    if (!isAnimating) return undefined;
    const id = window.setTimeout(() => {
      isAnimatingRef.current = false;
      setIsAnimating(false);
    }, SLIDE_MS + 80);
    return () => window.clearTimeout(id);
  }, [isAnimating, startIndex]);

  if (!loading && items.length === 0) return null;

  const slideOffset = startIndex * (cellWidth + itemGap);
  const trackStyle = isCarousel && cellWidth > 0
    ? { transform: `translate3d(-${slideOffset}px, 0, 0)` }
    : undefined;

  return (
    <section className="section partners-section" id="partners" ref={innerRef}>
      <div className="container">
        <h2 className="partners-title">
          <span className="partners-title-line" aria-hidden="true" />
          <span className="partners-title-text">{t('partners_title')}</span>
          <span className="partners-title-line" aria-hidden="true" />
        </h2>

        {loading ? (
          <div className="partners-loading">
            <div className="spinner" />
          </div>
        ) : (
          <div
            ref={panelRef}
            className={[
              'partners-carousel-panel',
              isCarousel && 'partners-carousel-panel--carousel',
              !isCarousel && 'partners-carousel-panel--static',
              isCompact && 'partners-carousel-panel--compact',
            ].filter(Boolean).join(' ')}
          >
            {isCarousel && (
              <button
                type="button"
                className={`partners-carousel-btn partners-carousel-btn--prev${canPrev ? '' : ' is-reserved'}`}
                onClick={() => slide(-1)}
                disabled={!canPrev || isAnimating}
                aria-label={t('partners_prev')}
                aria-hidden={!canPrev}
                tabIndex={canPrev ? 0 : -1}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            )}

            <div
              className="partners-carousel-viewport"
              ref={viewportRef}
              style={
                isCarousel
                  ? {
                      '--partner-slide-width': cellWidth > 0 ? `${cellWidth}px` : undefined,
                      width: viewportWidth > 0 ? `${viewportWidth}px` : 0,
                      minWidth: 0,
                      flex: '0 0 auto',
                    }
                  : undefined
              }
            >
              <ul
                className={[
                  'partners-carousel-track',
                  isCarousel ? 'partners-carousel-track--carousel' : 'partners-carousel-track--static',
                  isAnimating && 'is-animating',
                ].filter(Boolean).join(' ')}
                style={isCarousel ? trackStyle : undefined}
                onTransitionEnd={isCarousel ? handleTransitionEnd : undefined}
              >
                {items.map((sp, index) => (
                  <Fragment key={sp.id}>
                    {index > 0 && (
                      <li className="partner-carousel-divider" aria-hidden="true" />
                    )}
                    <li className="partner-carousel-cell">
                      <PartnerLogo sponsor={sp} />
                    </li>
                  </Fragment>
                ))}
              </ul>
            </div>

            {isCarousel && (
              <button
                type="button"
                className={`partners-carousel-btn partners-carousel-btn--next${canNext ? '' : ' is-reserved'}`}
                onClick={() => slide(1)}
                disabled={!canNext || isAnimating}
                aria-label={t('partners_next')}
                aria-hidden={!canNext}
                tabIndex={canNext ? 0 : -1}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
